import type { User, UserRole } from '../types'
import { usePermissionsStore } from '../store/permissionsStore'

/**
 * Oprávnění na frontendu (role jako data).
 *
 * Schopnost (capability) je řetězec „modul.akce". Uživatel má právě jednu roli firmy; její schopnosti
 * (efektivní, bez funkcí skrytých v nastavení firmy) vrací /auth/permissions a drží `permissionsStore`.
 *
 *   can(user, cap) = skrytá funkce → false; superuživatel → true; jinak cap ∈ capabilities
 *
 * Dokud se /auth/permissions nenačte, padá `can()` zpět na původní výchozí matici podle starého klíče
 * role (`user.role`). Backend (`app/Security/Permissions.php`) je autoritativní, katalog s popisky
 * posílá GET /admin/roles/catalog.
 */
export type Capability =
  // Moduly
  | 'module.attendance'
  | 'module.preparations'
  | 'module.tools'
  | 'module.tasks'
  | 'module.admin'
  | 'module.reports'
  | 'module.revisions'
  | 'module.inbox'
  // Doklady z e-mailu
  | 'inbox.exportPohoda'
  | 'module.vehicles'
  // Docházka
  | 'attendance.own'
  | 'attendance.planning'
  | 'attendance.selfPlanOrders'
  | 'attendance.payroll'
  | 'attendance.payrollEdit'
  | 'attendance.orderReport'
  | 'attendance.ordersManage'
  | 'attendance.ordersAdmin'
  | 'attendance.editClosedOrders'
  | 'attendance.timeAnyOrder'
  | 'attendance.absenceApprove'
  | 'attendance.absenceManageAll'
  | 'attendance.viewOthers'
  | 'attendance.overtimeView'
  | 'attendance.overtimeManage'
  | 'attendance.vacationManage'
  // Zakázky
  | 'orders.create'
  | 'orders.economy'
  | 'orders.hierarchy'
  | 'orders.invoice'
  // Servisní protokoly
  | 'protocols.manage'
  // Přípravy
  | 'preparations.create'
  | 'preparations.editDraft'
  | 'preparations.fullManage'
  | 'preparations.adminTransitions'
  | 'preparations.reexportPohoda'
  // Půjčovna nářadí
  | 'tools.borrowReturn'
  | 'tools.manage'
  | 'tools.delete'
  // Vozový park
  | 'vehicles.manage'
  | 'vehicles.delete'
  // Úkoly
  | 'tasks.createOwn'
  | 'tasks.createForOthers'
  | 'tasks.viewAll'
  | 'tasks.manageAll'
  | 'tasks.deleteOwn'
  | 'tasks.manageStatuses'
  // Reporty pro majitele
  | 'reports.view'
  | 'reports.invoiceMatching'
  // Administrace
  | 'admin.settings'
  | 'admin.users'
  | 'admin.audit'
  | 'admin.statuses'
  | 'admin.emailTemplates'
  | 'admin.absenceSettings'
  | 'admin.rolesPermissions'
  // Rozšířená oprávnění
  | 'admin.impersonate'

// --- Záloha do načtení /auth/permissions: původní výchozí matice pevných rolí ---

const MANAGER_CAPS: Capability[] = [
  'module.attendance', 'module.preparations', 'module.tools', 'module.tasks', 'module.admin',
  'attendance.own', 'attendance.planning', 'attendance.overtimeView',
  'orders.create',
  'preparations.create', 'preparations.editDraft', 'preparations.fullManage',
  'tools.borrowReturn', 'tools.manage',
  'tasks.createOwn', 'tasks.createForOthers', 'tasks.deleteOwn',
  'admin.settings',
]

const WAREHOUSE_CAPS: Capability[] = [
  'module.attendance', 'module.preparations', 'module.tools', 'module.tasks', 'module.admin',
  'attendance.own', 'attendance.planning',
  'attendance.payroll', 'attendance.orderReport', 'attendance.ordersManage',
  'attendance.absenceApprove', 'attendance.overtimeView', 'attendance.overtimeManage',
  'attendance.timeAnyOrder', 'attendance.absenceManageAll', 'protocols.manage',
  'orders.create',
  'preparations.create', 'preparations.editDraft', 'preparations.fullManage',
  'preparations.reexportPohoda',
  'tools.borrowReturn', 'tools.manage',
  'tasks.createOwn', 'tasks.createForOthers', 'tasks.deleteOwn',
  'admin.settings',
]

const WORKER_CAPS: Capability[] = [
  'module.attendance', 'module.preparations', 'module.tools', 'module.tasks',
  'attendance.own',
  'preparations.create', 'preparations.editDraft',
  'tools.borrowReturn',
  'tasks.createOwn',
]

const SWITCHBOARD_CAPS: Capability[] = [
  'module.attendance',
  'attendance.own', 'attendance.selfPlanOrders', 'tasks.deleteOwn', 'orders.create',
]

const FALLBACK_MATRIX: Partial<Record<UserRole, Capability[]>> = {
  MARKETING: [...MANAGER_CAPS, 'attendance.selfPlanOrders'],
  MANAGER: MANAGER_CAPS,
  WAREHOUSE: WAREHOUSE_CAPS,
  WORKER: WORKER_CAPS,
  SWITCHBOARD: SWITCHBOARD_CAPS,
}

/** Jsou ve store načtená oprávnění právě tohoto uživatele? */
function loadedFor(user: User): boolean {
  const { userId } = usePermissionsStore.getState()
  return userId !== null && userId === user.id
}

/**
 * Superuživatel (role s příznakem is_admin) — má vždy vše kromě skrytých funkcí, nejde mu nic odebrat.
 * Jediná povolená kontrola „podle role"; vše ostatní jde přes `can()`.
 */
export function isSuperuser(user: User | null | undefined): boolean {
  if (!user) return false
  if (loadedFor(user)) return usePermissionsStore.getState().isAdmin
  return user.role === 'ADMIN'
}

/**
 * Vrátí true, pokud má uživatel danou schopnost. Bez uživatele (nepřihlášen) vrací false.
 */
export function can(user: User | null | undefined, capability: Capability): boolean {
  if (!user) return false
  if (isSuperuser(user)) return true

  if (loadedFor(user)) {
    return usePermissionsStore.getState().capabilities.includes(capability)
  }
  return FALLBACK_MATRIX[user.role]?.includes(capability) ?? false
}

/**
 * Dříve skrývatelné funkce firmy. Skrývání bylo zrušeno — moduly řídí jen matice oprávnění rolí,
 * server posílá `hidden_features: []`. Typ a funkce zůstávají kvůli místům, která se ptají.
 */
export type HideableFeature = 'reports' | 'overtime'

/** Vždy false — skrývání funkcí bylo zrušeno. */
export function isFeatureHidden(_feature: HideableFeature): boolean {
  return false
}
